import React, { useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { i18nIntl, isNotEmpty, migrate_question} from '../../lib/ep.js';

import ActionButton, { SecondaryButton } from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout.js';

import CheckEnabler from '../builder/CheckEnabler.js';

import SectionTitle from '../builder/SectionTitle.js';
import { v4 as uuidv4 } from 'uuid';
import MatchItClient from '../front/MatchItClient.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import Tooltip from '../builder/Tooltip.js';
import SimpleUploadedGallery from '../builder/SimpleUploadedGallery.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const AddImageLayer = (props) => {
	const [ pair, setPair ] = useState([ {}, {} ]);
	const pair_id = useRef(uuidv4());

	const changeMulti = (values) => {
		const index = (values?.image_1) ? 0 : 1;
		const image = (values?.image_1) ? values?.image_1 : values.image_2;
		const full = (values?.full_image_1) ? values?.full_image_1 : values.full_image_2;

		setPair((state) => {
			state[index] = {
				name:pair_id.current,
				index,
				image,
				full,
			};

			return [...state];
		});
	};

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			backButton={props.close}
		>
			<div>
				<SectionTitle>Add more matching images</SectionTitle>

				<div>
					<Model model={pair}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name='image_1'
							changeMulti={changeMulti}
							image={(pair[0]?.image) ? pair[0].full : ''}
							small_input={true}
							_class={{ size:60, first:true }}
							_size={{ width:200, height:200 }}
						>Image 1</AutoPostImageUploader>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							changeMulti={changeMulti}
							name='image_2'
							image={(pair[1]?.image) ? pair[1].full : ''}
							small_input={true}
							_class={{ size:60 }}
							_size={{ width:200, height:200 }}
						>Image 2</AutoPostImageUploader>
					</Model>
				</div>

				<ButtonCollection in_floating={true}>
					<ActionButton
						disabled={(pair.length != 2) ? true : false}
						onClick={(e, cb) => props.onSubmit(pair, cb)}
					>Accept</ActionButton>

					<SecondaryButton
						onClick={props.close}
					>Close</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors_game, setErrorsGame ] = useState({});
	const [ display_add_layer, setDisplayAddLayer ] = useState(false);

	const min_images = 3;
	const max_images = 8;

	const getMinAttemps = () => {
		const level = stage?.puzzle_level ?? 'easy';

		switch(level) {
			case 'hard':
				return 5;

			case 'normal':
				return 4;

			case 'easy':
				return 3;
		}

		return 3;
	};

	const changeMultiImages = (data) => {
		if(stage?.memory_images == undefined) stage.memory_images = [];
		change('memory_images',[...stage.memory_images,{image:data.memory_images,full:data.full_memory_images}]);
	}

	const delete_image = (image_name) => {
		change('matchit_images',stage?.matchit_images.filter(img => (img[0].image != image_name)));
	}

	const addImages = (pair, cb) => {
		const images = stage.matchit_images ?? [];
		change('matchit_images', [ ...images, pair ]);
		if(cb != undefined) cb();
		setDisplayAddLayer(false);
	};

	const min_attempts = Math.pow(getMinAttemps(), 2);

	const distribution_mode = [
		{ k:'separation', v:i18n("In columns") },
		{ k:'mixed', v:i18n("Mixed") },
	];

	const images = stage?.matchit_images ?? [];

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
				errors={(Object.keys(errors_game).length > 0) ? true : false}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model
						model={stage}
						change={change}
					>
						<Range
							min={30}
							max={300}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							name="game_time"
							value={Math.max((stage?.game_time ?? 60), 30)}
							_class={{ first:true }}
						>Time limit</Range>

						<CheckEnabler 
							label="Limit moves"
							enabled={stage?.game_attempts > 0}
							onDisable={() => change('game_attempts', "")}
							onEnable={() => change('game_attempts', min_attempts)}
						>
							<Range
								model={stage}
								change={change}
								min={min_attempts}
								max={50}
								step={1}
								name="game_attempts"
								value={Math.max((stage?.game_attempts ?? 1), min_attempts)}
							>Max. number of attempts/moves</Range>
						</CheckEnabler>

						<Select
							_class={{ size:70 }}
							name="matchit_distribution_mode"
							values={distribution_mode}
						>Play mode</Select>

						<Checkbox
							name="matchit_display_cards_backfliped"
							value={stage?.matchit_display_cards_backfliped ?? true}
						>Display cards face down</Checkbox>

						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title={`${i18n("Images")} (${images.length} / ${max_images})`}
						icon='knk-picture'
						title_actions={<ActionButton
							onClick={() => setDisplayAddLayer(true)}
							disabled={(images.length >= max_images) ? true : false}
						>New pair</ActionButton>}
						autotranslate={false}
					/>

					<SimpleUploadedGallery
						items={images}
						min_items={min_images}
						max_items={max_images}
						onDelete={delete_image}
					/>
				</article>

				{(display_add_layer) && (
					<AddImageLayer
						promotion={props.promotion}
						type='images'
						close={() => setDisplayAddLayer(false)}
						onSubmit={addImages}
					/>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors_game).length == 0) ? (
							<MatchItClient
								stage={{ Stage:stage }}
								is_builder={true}
								is_playable={false}
								section="settings"
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors_game).map((error, i) => {
										if(typeof error === 'object') return null;

										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const distribution_mode = stage?.matchit_distribution_mode ?? 'separation';
	const cards_backfliped = stage?.matchit_display_cards_backfliped ?? true;

	const separated_sections = (distribution_mode == 'separation') ? true : false;

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				{(cards_backfliped && separated_sections) && (
					<>
						<article>
							<BuilderSubSectionTitle
								title="Cards (left column)"
								icon='knk-sticker'
							/>

							<Model model={stage} change={change}>
								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${props.promotion.id}`}
									auto_type='stages'
									name="matchit_backflip1_image"
									image={stage.full_matchit_backflip1_image}
									display_custom_dragging_instructions="Upload the image"
									small_input={true}
									_size={{ width:200, height:200 }}
								>Reverse side image</AutoPostImageUploader>

								{(!isNotEmpty(stage, 'full_matchit_backflip1_image')) && (
									<ColorPicker
										name="matchit_backflip1_bg_color"
										color={stage?.matchit_backflip1_bg_color ?? 'rgba(120, 195, 0, 1)'}
										_class={{ size:50 }}
									>Background color</ColorPicker>
								)}
							</Model>
						</article>

						<article class="gui_not_in_top_huge">
							<BuilderSubSectionTitle
								title="Cards (right column)"
								icon='knk-sticker'
							/>

							<Model model={stage} change={change}>
								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${props.promotion.id}`}
									auto_type='stages'
									name='matchit_backflip2_image'
									image={(stage?.matchit_backflip2_image) ? stage.full_matchit_backflip2_image : null}
									display_custom_dragging_instructions="Upload the image"
									small_input={true}
									_size={{ width:200, height:200 }}
								>Reverse side image</AutoPostImageUploader>

								{(!isNotEmpty(stage, 'full_matchit_backflip2_image')) && (
									<ColorPicker
										name="matchit_backflip2_bg_color"
										color={stage?.matchit_backflip2_bg_color ?? 'rgba(120, 195, 0, 1)'}
										_class={{ size:50 }}
									>Background color</ColorPicker>
								)}
							</Model>
						</article>
					</>
				)}

				<article class={(cards_backfliped && separated_sections) ? 'gui_not_in_top_huge' : null}>
					<BuilderSubSectionTitle
						title="Cards"
						icon='knk-sticker'
					/>

					{(cards_backfliped && !separated_sections) && (
						<Model model={stage} change={change}>
							<AutoPostImageUploader
								auto_url={`/promotions/auto_img/${props.promotion.id}`}
								auto_type='stages'
								name="matchit_backflip1_image"
								image={stage.full_memory_backflip_image}
								display_custom_dragging_instructions="Upload the image"
								small_input={true}
								_size={{ width:200, height:200 }}
							>Reverse side image</AutoPostImageUploader>

							{(!isNotEmpty(stage, 'full_matchit_backflip1_image')) && (
								<ColorPicker
									name="matchit_backflip1_bg_color"
									color={stage?.matchit_backflip1_bg_color ?? 'rgba(120, 195, 0, 1)'}
									_class={{ size:50 }}
								>Background color</ColorPicker>
							)}
						</Model>
					)}

					<Model model={stage} change={change}>
						<Range
							min={0}
							max={10}
							name="matchit_card_border_padding"
							default={7}
							step={1}
							_class={{ size:50 }}
							value={stage?.matchit_card_border_padding ?? 7}
							label_props={{
								_extra_component:i18n("in 'px'"),
							}}
						>Border padding</Range>

						{(parseInt(stage?.matchit_card_border_padding ?? 7) > 0) && (
							<ColorPicker
								name="matchit_card_padding_color"
								color={stage?.matchit_card_padding_color ?? 'rgba(255, 255, 255, 1)'}
								_class={{ size:50 }}
							>Padding color</ColorPicker>
						)}

						<Range
							min={0}
							max={3}
							name="matchit_card_border_width"
							default={2}
							step={1}
							_class={{ size:50 }}
							value={stage?.matchit_card_border_width ?? 2}
							label_props={{
								_extra_component:i18n("in 'px'"),
							}}
						>Border width</Range>

						{(parseInt(stage?.matchit_card_border_width ?? 2) > 0) && (
							<ColorPicker
								name="matchit_card_border_color"
								_class={{ size:50 }}
								_hide_code={true}
								color={stage?.matchit_card_border_color ?? 'rgba(0, 0, 0, 1)'}
							>Border color</ColorPicker>
						)}

						<Range
							min={0}
							max={100}
							name="matchit_card_border_radius"
							default={50}
							step={10}
							_class={{ size:40 }}
							value={stage?.matchit_card_border_radius ?? 50}
							label_props={{
								_extra_component:i18n("in 'px'"),
							}}
						>Border radius</Range>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Board and behavior"
						icon='knk-page'
					/>

					<Model model={stage} change={change}>
						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name="bg_image"
							image={stage.full_bg_image}
							display_custom_dragging_instructions="Upload the image"
							small_input={true}
							_size={{ width:800, height:800 }}
						>Background image</AutoPostImageUploader>

						{(!isNotEmpty(stage, 'full_bg_image')) && (
							<ColorPicker
								name="bg_color"
								_class={{ size:50 }}
								color={stage?.bg_color ?? 'rgba(255, 255, 255, 1)'}
							>Background color</ColorPicker>
						)}
 
						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>

						<Checkbox
							name='matchit_apply_entropy'
							label_props={{
								tooltip:<Tooltip
									position='top'
								>If you enable this option, the cards will be slightly shifted and rotated with each movement to simulate a real board game.</Tooltip>
							}}
						>Apply entropy</Checkbox>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<MatchItClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							section="settings"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<MatchItClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const MatchItBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/matchit`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const migrate_from_question = true;
	if(migrate_from_question) {
		const question = props.questionStore.getState()?.questions.questions.find((q) => (q.Question.stage == props.stage.id));

		const game_stage_fields_map = {
			images:'matchit_images',
			backflip1:'matchit_backflip1_image',
			backflip2:'matchit_backflip2_image',
			time_seconds:'game_time',
			attempts:'game_attempts',
			bg_color:'bg_color'
		}

		if(question) {
			migrate_question(game_stage_fields_map, stage, question.Question);
		}
	};

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<MatchItClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['fail']}
						>
							<MatchItClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(MatchItBuilder);